import React from 'react'
import { Skeleton, Typography, Button, Row, Col, Form, Input, InputNumber, Switch, Divider, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import TableTransfer from '../../TableTransfer'
import IShelterTax from '../../../interfaces/IShelterTax'
import IShelter from '../../../interfaces/IShelter'
import { SearchRequest, ShelterTaxSaveRequest }from '../../../services/api/interfaces/ApiRequest'

type ShelterTaxProps = BaseFormComponentProps & {
  uuid?: string,
  data?: IShelterTax | null,
  shelterUuid: string,
  error: any | null,

  shelter: IShelter | null,

  loading: boolean,
  history: any,

  loadTax: (args: SearchRequest) => void,

  dispatchCreate: (args: ShelterTaxSaveRequest) => void,
  dispatchUpdate: (args: ShelterTaxSaveRequest) => void
}

export default class ShelterTax extends FormComponent<ShelterTaxProps> {

  state = {
    saving: false,
    loaded: false,
    branchesSelected: [] as string[],
    branchesSelection: {
      sourceSelectedKeys: [] as any,
      targetSelectedKeys: [] as any
    }
  }

  // Transfer branches columns
  transferBranchesColumns = {
    left: [
      {
        dataIndex: 'name',
        title: 'Name',
        searchable: true
      }
    ],
    right: [
      {
        dataIndex: 'name',
        title: 'Name'
      },
      {
        dataIndex: 'price',
        title: 'Price',
        render: (text: any, record: any) => {
          return (
            <div style={{width: 120}}>
              <Form.Item
                name={['branches', record.key, 'price']}
                rules={[
                  {
                    required: true,
                    message: 'Missing price'
                  }
                ]}
              >
                <InputNumber
                  placeholder="Price by location"
                  min={0}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </div>
          )
        }
      },
      {
        dataIndex: 'active',
        title: 'Active',
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={['branches', record.key, 'active']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )
      },
      {
        dataIndex: 'required',
        title: 'Required',
        render: (text: any, record: any) => (
          <div>
            <Form.Item
              name={['branches', record.key, 'required']}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>
        )
      }
    ]
  }

  componentDidMount() {
    const { uuid, loadTax } = this.props

    // Check permissions
    this.validateAccessPermissions()


    // Load tax if we are editing
    if (uuid) {
      loadTax({
        search: {
          uuid,
          'tax_branch': {
            with: {
              deleted: false,
              branch: {
                with: true
              }
            }
          }
        }
      })
    }
  }

  componentDidUpdate(prevProps: ShelterTaxProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded
    if (!this.state.loaded && (wasLoading || !this.props.uuid)) {
      newState.loaded = true

      // Set selected branches and species
      if (this.props.uuid && this.props.data) {
        const { tax_branch } = this.props.data

        tax_branch?.forEach(p => {
          if (!newState.branchesSelected.includes(p.branch.uuid)) {
            newState.branchesSelected.push(p.branch.uuid)
          }
        })
      }
    }

    // We are saving
    if (this.state.saving && wasLoading) {
      // Add the new role name to url
      if (!this.props.uuid && this.props.data) {
        this.props.history.replace(`/organization/shelter/${this.props.shelterUuid}/tax/${this.props.data.uuid}`)
      }

      if (this.props.error) {
        message.error(`Failed to save tax. Reason: ${this.props.error}`)
      } else {
        message.success('Tax saved successfully!')
      }

      newState.saving = false
    }

    // Update if changed
    if (newState.loaded !== this.state.loaded || newState.saving !== prevState.saving) {
      this.setState(newState)
    }
  }

  handleBranchSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      branchesSelected: targetKeys
    })
  }

  handleSelectionChange = (transferName: string, sourceSelectedKeys: string[], targetSelectedKeys: string[]) => {
    this.setState(state => ({
      ...state,
      [transferName]: {
        sourceSelectedKeys,
        targetSelectedKeys
      }
    }))
  }

  handleSave = (values) => {
    const { uuid, shelterUuid, dispatchCreate, dispatchUpdate } = this.props
    const { branchesSelected } = this.state

    let payload = {
      organization_tax: {
        ...values
      },
      organization_tax_branch: branchesSelected.map(branchUuid => {
        return {
          branch_uuid: branchUuid,
          price: values.branches[branchUuid].price || null,
          active: values.branches[branchUuid].active || false,
          required: values.branches[branchUuid].required || false
        }
      }) as any
    }

    // Remove unnecessary fields
    delete payload.organization_tax.branches

    if (!uuid) {
      payload.organization_tax['shelter_uuid'] = shelterUuid
      
      dispatchCreate(payload)
      this.setState({
        saving: true
      })
    } else {
      payload = {
        ...payload,
        organization_tax: {
          ...payload.organization_tax,
          uuid
        }
      }

      dispatchUpdate(payload)
      this.setState({
        saving: true
      })
    }
  }

  render() {
    const { uuid, shelter, data, loading } = this.props
    const { loaded, branchesSelected } = this.state

    if (!loaded || !shelter) {
      return <Skeleton />
    }

    let taxBranches = {} as any
    const branchOptions = shelter?.organization_branches?.map(branch => {
      const selected = branchesSelected.includes(branch.uuid)
      const taxBranch = data?.tax_branch?.find(x => x.branch.uuid === branch.uuid)

      if (selected && taxBranch) {
        taxBranches[branch.uuid] = taxBranch
      }

      return {
        key: branch.uuid,
        name: branch.name,
        price: selected && taxBranch ? taxBranch.price : 0,
        active: selected && taxBranch ? taxBranch.active : false,
        required: selected && taxBranch ? taxBranch.required : false,
        chosen: selected !== undefined
      }
    }) || []

    let values:any = {
      description: ''
    }

    if (uuid) {
      values = {
        // Default values
        required: false,
        active: false,

        ...data || {},
        branches: taxBranches
      }
    } else {
      values = {
        // Default values
        required: false,
        active: false
      }
    }

    return (
      <Form
        name="organization_tax"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Row gutter={12}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Missing a name for the tax'
                }
              ]}
            >
              <Input />
            </Form.Item>
            
            <Row gutter={12}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Price"
                  name="price"
                  rules={[
                    {
                      required: true,
                      message: 'Missing a price for the tax'
                    }
                  ]}
                >
                  <InputNumber
                    placeholder="Price"
                    min={0}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  label="Active"
                  name="active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  label="Required"
                  name="required"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                {
                  required: true,
                  message: 'Missing a description for the tax'
                }
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
        
        <Divider />

        <Row>
          <Col xs={24}>
            <Typography.Title level={5}>Locations</Typography.Title>
            <TableTransfer
              dataSource={branchOptions}
              targetKeys={branchesSelected}
              listStyle={null}
              leftColumns={this.transferBranchesColumns.left}
              rightColumns={this.transferBranchesColumns.right}
              onChange={this.handleBranchSelection}
              onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]) => this.handleSelectionChange('branchesSelection', sourceSelectedKeys, targetSelectedKeys)}
            />
          </Col>
        </Row>

        <Divider />

        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
